import React, { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { validateFile, formatFileSize } from '../../../utils/fileUtils';

/**
 * FileUploadZone component provides a drag-and-drop and click-to-upload interface for Excel files.
 *
 * @component
 * @param {File|null} file - The currently selected file, or null if none is selected.
 * @param {function} onFileSelect - Callback invoked when a file is selected or changed. Receives the File object or null.
 * @param {function} onReset - Callback invoked when the file is removed/reset.
 * @returns {JSX.Element} The file upload UI for Excel files.
 */
const FileUploadZone = ({ file, onFileSelect, onReset }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) {
      onFileSelect(null);
      return;
    }

    const validation = validateFile(selectedFile);
    if (!validation.isValid) {
      toast.error(validation.error);
      onFileSelect(null);
      return;
    }

    onFileSelect(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const fakeEvent = {
        target: { files: droppedFiles }
      };
      handleFileSelect(fakeEvent);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    onReset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-4">
      <h6 style={{ color: '#74E8C7', fontSize: '16px' }}>Step 2: Upload Completed File</h6>
      <div
        className={`border-2 border-dashed rounded p-4 text-center ${file ? 'border-success' : ''}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          minHeight: '120px',
          cursor: 'pointer',
          backgroundColor: file ? 'rgba(116, 232, 199, 0.1)' : 'rgba(116, 232, 199, 0.05)',
          borderColor: file ? '#74E8C7' : '#74E8C7',
          borderRadius: '10px',
          color: '#d6d8e1'
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".xlsx"
          style={{ display: 'none' }}
        />

        {file ? (
          <div>
            <FontAwesomeIcon icon={faFileExcel} size="2x" className="mb-2" style={{ color: '#74E8C7' }} />
            <div className="font-weight-bold" style={{ color: 'white' }}>{file.name}</div>
            <div style={{ color: '#d6d8e1' }}>
              {formatFileSize(file.size)}
            </div>
            <Button
              variant="link"
              size="sm"
              onClick={handleRemoveFile}
              className="d-flex align-items-center justify-content-center"
              style={{
                color: '#74E8C7',
                padding: '2px 4px',
                minHeight: '20px',
                fontSize: '10px'
              }}
            >
              Remove
            </Button>
          </div>
        ) : (
          <div>
            <FontAwesomeIcon icon={faUpload} size="2x" className="mb-2" style={{ color: '#74E8C7' }} />
            <div style={{ color: 'white' }}>Click to select or drag and drop your Excel file here</div>
            <div className="small" style={{ color: '#d6d8e1' }}>
              Supports .xlsx files up to 50MB
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadZone;
